import { useState } from "react";
import { Check, CheckCheck, CircleAlert, Clock, Info } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatReceiptTime } from "@/lib/messages/messages-helpers";
import {
  DELIVERY_STATE,
  computeMessageDeliveryStatus,
  hasDeliveryDetail,
  readChannelReceipts,
} from "@/lib/messages/message-delivery-status";
import { cn } from "@/lib/utils";

// No i18n framework in this app (see package.json — no i18next/react-intl/lingui, and every
// string in the Messages UI is an inline literal). Collected here so a future extraction is one
// file rather than a hunt.
const T = {
  title: "Message info",
  sent: "Sent",
  seen: "Seen",
  delivered: "Delivered",
  notDelivered: "Not delivered yet",
  failed: "Failed to send",
  none: "No delivery information yet.",
  whatsappNote: "WhatsApp reports status for the chat, not per person.",
  readReceiptsOff: "The recipient may have read receipts turned off.",
  deliveredMeaning: "Delivered means a signed-in Healo app received the message.",
  ariaOpen: "Message delivery info",
  aria: {
    [DELIVERY_STATE.sending]: "Sending",
    [DELIVERY_STATE.sent]: "Sent",
    [DELIVERY_STATE.delivered]: "Delivered",
    [DELIVERY_STATE.seen]: "Seen",
    [DELIVERY_STATE.failed]: "Failed to send",
  },
};

function countLabel(n, total) {
  return total == null ? `${n}` : `${n} of ${total}`;
}

/** The tick glyph for a state, or null when nothing should render. */
function TickGlyph({ state }) {
  switch (state) {
    case DELIVERY_STATE.failed:
      return <CircleAlert className="h-3.5 w-3.5 text-destructive" aria-hidden />;
    case DELIVERY_STATE.sending:
      return <Clock className="h-3.5 w-3.5 opacity-70" aria-hidden />;
    case DELIVERY_STATE.sent:
      return <Check className="h-3.5 w-3.5" aria-hidden />;
    case DELIVERY_STATE.delivered:
      return <CheckCheck className="h-3.5 w-3.5" aria-hidden />;
    case DELIVERY_STATE.seen:
      return <CheckCheck className="h-3.5 w-3.5 text-primary" aria-hidden />;
    default:
      return null;
  }
}

function ReceiptRow({ userId, at, memberDirectory }) {
  const entry = memberDirectory?.[userId];
  // Fall back to the raw id with underscores softened, mirroring getSenderLabel.
  const label = entry?.label || String(userId || "").replace(/_/g, " ");
  const initial = label.trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="flex items-center gap-2 py-1">
      <Avatar className="h-5 w-5 shrink-0">
        {entry?.avatarUrl ? <AvatarImage src={entry.avatarUrl} alt="" /> : null}
        <AvatarFallback className="text-[9px]">{initial}</AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate text-xs">{label}</span>
      <span className="shrink-0 text-[11px] text-muted-foreground">{formatReceiptTime(at) || "—"}</span>
    </div>
  );
}

function Section({ heading, count, total, children }) {
  return (
    <div className="border-t border-border/60 px-3 py-2 first:border-t-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{heading}</span>
        {count != null ? (
          <span className="text-[11px] text-muted-foreground">{countLabel(count, total)}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * WhatsApp GROUP mode: per-participant rows from `message.channelReceipts`, split into
 * Read / Delivered / Pending the way the per-user sections are — groups DO give us per-person
 * receipts, unlike the 1:1 channel status. Names resolve jid → roster displayName through the
 * caller-built map; an unresolved jid falls back to its local part (the phone digits), which is
 * still an identity, not a blank. No avatars — the roster has none to offer.
 */
function WaGroupReceiptSections({ receipts, namesByJid }) {
  const label = (jid) => {
    const key = String(jid || "").toLowerCase();
    return (namesByJid && namesByJid[key]) || String(jid || "").split("@")[0] || "?";
  };
  const seen = receipts.filter((r) => Boolean(r.readAt));
  const delivered = receipts.filter((r) => !r.readAt && Boolean(r.deliveredAt));
  const pending = receipts.filter((r) => !r.readAt && !r.deliveredAt);
  const total = receipts.length;

  const row = (r, at) => (
    <div key={r.jid} className="flex items-baseline justify-between gap-2 py-1">
      <span className="min-w-0 flex-1 truncate text-xs">{label(r.jid)}</span>
      <span className="shrink-0 text-[11px] text-muted-foreground">{formatReceiptTime(at) || "—"}</span>
    </div>
  );

  return (
    <ScrollArea viewportClassName="max-h-72">
      {seen.length > 0 ? (
        <Section heading={T.seen} count={seen.length} total={total}>
          {seen.map((r) => row(r, r.readAt))}
        </Section>
      ) : null}
      {delivered.length > 0 ? (
        <Section heading={T.delivered} count={delivered.length} total={total}>
          {delivered.map((r) => row(r, r.deliveredAt))}
        </Section>
      ) : null}
      {pending.length > 0 ? (
        <Section heading={T.notDelivered} count={pending.length}>
          {pending.map((r) => row(r, ""))}
        </Section>
      ) : null}
    </ScrollArea>
  );
}

/** Channel-level rows for a WhatsApp thread: no avatars, no names — WhatsApp gives us neither. */
function ChannelRows({ channel }) {
  const rows = [
    [T.sent, channel?.sentAt],
    [T.delivered, channel?.deliveredAt],
    [T.seen, channel?.readAt || channel?.playedAt],
  ].filter(([, at]) => Boolean(at));
  if (channel?.failedAt) {
    rows.push([T.failed, channel.failedAt]);
  }

  return (
    <>
      {rows.map(([label, at]) => (
        <div key={label} className="flex items-baseline justify-between gap-2 px-3 py-1.5">
          <span className="text-xs">{label}</span>
          <span className="text-[11px] text-muted-foreground">{formatReceiptTime(at)}</span>
        </div>
      ))}
      <p className="px-3 pb-2 pt-1 text-[11px] leading-snug text-muted-foreground">{T.whatsappNote}</p>
      {channel?.deliveredAt && !channel?.readAt && !channel?.playedAt ? (
        <p className="px-3 pb-2 text-[11px] leading-snug text-muted-foreground">{T.readReceiptsOff}</p>
      ) : null}
    </>
  );
}

/**
 * Per-message delivery/seen indicator. The tick cluster IS the trigger — WhatsApp muscle memory,
 * and a real <button> so it is keyboard reachable. Renders nothing when there is no state to show
 * (someone else's message with no receipts, or a message with no server id).
 *
 * Content is rendered entirely from data already on the message: history and the live receipt
 * delta both carry `readBy` / `deliveredTo` / `channelStatus`, so opening this fetches nothing.
 *
 * Imports Popover from @/components/ui/popover, NOT @radix-ui/react-popover directly: the local
 * wrapper portals into useOverlayContainer(), without which the popover survives the Electron
 * workspace-pane freeze and floats over whatever the user switches to.
 */
export function MessageReceiptsPopover({
  message,
  isOwn,
  recipientIds,
  primaryRecipientIds,
  isWhatsAppThread,
  memberDirectory,
  // WhatsApp group thread: the detail view lists channelReceipts per participant (names via
  // waGroupNamesByJid) instead of the anonymous chat-level rows. The TICK itself is untouched —
  // it keeps reading the aggregate channelStatus.
  isWaGroupThread = false,
  waGroupNamesByJid = null,
  className,
  forceIcon,
}) {
  const [open, setOpen] = useState(false);
  const status = computeMessageDeliveryStatus({
    message,
    isOwn,
    recipientIds,
    primaryRecipientIds,
    isWhatsAppThread,
  });

  // forceIcon renders an explicit "info" affordance instead of a tick — used by the gutter, which
  // must also work on incoming messages (where there is no tick state at all).
  const asInfo = forceIcon === "info";
  if (!asInfo && status.state === DELIVERY_STATE.none) {
    return null;
  }
  // A placeholder still in flight has nothing to inspect, and the bubble already conveys it.
  const interactive = asInfo || status.state !== DELIVERY_STATE.sending;
  const glyph = asInfo ? <Info className="h-4 w-4" aria-hidden /> : <TickGlyph state={status.state} />;
  if (!glyph) {
    return null;
  }
  if (!interactive) {
    return (
      <span
        className={cn("inline-flex items-center text-muted-foreground", className)}
        aria-label={T.aria[status.state]}
        data-capture-ignore="true"
      >
        {glyph}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-capture-ignore="true"
          aria-label={asInfo ? T.ariaOpen : `${T.aria[status.state]} — ${T.ariaOpen}`}
          title={asInfo ? T.title : T.aria[status.state]}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center rounded text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          {glyph}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-72 p-0" data-capture-ignore="true">
        <MessageReceiptsDetail
          message={message}
          isOwn={isOwn}
          recipientIds={recipientIds}
          primaryRecipientIds={primaryRecipientIds}
          isWhatsAppThread={isWhatsAppThread}
          memberDirectory={memberDirectory}
          isWaGroupThread={isWaGroupThread}
          waGroupNamesByJid={waGroupNamesByJid}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The panel body on its own, so it can be shown somewhere other than this component's popover —
 * the per-message actions menu embeds it rather than opening a popover inside a popover, which
 * Radix would resolve by closing the outer one the moment the inner is touched.
 *
 * Recomputes the status from the message rather than taking it as a prop: every caller has the
 * message and the same inputs, and it is a pure function of data already loaded.
 */
export function MessageReceiptsDetail({
  message,
  isOwn,
  recipientIds,
  primaryRecipientIds,
  isWhatsAppThread,
  memberDirectory,
  isWaGroupThread = false,
  waGroupNamesByJid = null,
  showTitle = true,
}) {
  const status = computeMessageDeliveryStatus({
    message,
    isOwn,
    recipientIds,
    primaryRecipientIds,
    isWhatsAppThread,
  });
  const detail = hasDeliveryDetail(status);
  // Group mode only when the message actually carries per-participant receipts — a group message
  // acked before the backend recorded any participant rows still shows the chat-level rows.
  const waGroupReceipts = isWaGroupThread ? readChannelReceipts(message) : [];
  const waGroupMode = waGroupReceipts.length > 0;
  return (
    <>
      {showTitle ? <div className="px-3 pb-1 pt-2 text-xs font-semibold">{T.title}</div> : null}

      {!detail && !waGroupMode ? (
        <p className="px-3 pb-3 text-xs text-muted-foreground">{T.none}</p>
      ) : null}

      {waGroupMode ? (
        <WaGroupReceiptSections receipts={waGroupReceipts} namesByJid={waGroupNamesByJid} />
      ) : null}

      {/* Branch on the computed mode, not the raw prop: a message carrying a channelStatus is
          channel-shaped even when the thread row could not be resolved as WhatsApp. */}
      {!waGroupMode && detail && status.channelMode ? <ChannelRows channel={status.channel} /> : null}

      {detail && !status.channelMode ? (
        <>
          {/* Sections in WhatsApp's order (seen above delivered); empty ones are omitted. A
              large roster would otherwise run off the screen, hence the scroll cap. */}
          <ScrollArea viewportClassName="max-h-72">
            {status.seen.length > 0 ? (
              <Section
                heading={T.seen}
                count={status.recipientCount == null ? null : status.seen.length}
                total={status.recipientCount}
              >
                {status.seen.map((r) => (
                  <ReceiptRow key={`seen-${r.userId}`} userId={r.userId} at={r.at} memberDirectory={memberDirectory} />
                ))}
              </Section>
            ) : null}

            {status.delivered.length > 0 ? (
              <Section
                heading={T.delivered}
                count={status.recipientCount == null ? null : status.delivered.length}
                total={status.recipientCount}
              >
                {status.delivered.map((r) => (
                  <ReceiptRow key={`del-${r.userId}`} userId={r.userId} at={r.at} memberDirectory={memberDirectory} />
                ))}
              </Section>
            ) : null}

            {status.pending.length > 0 ? (
              <Section heading={T.notDelivered} count={status.pending.length}>
                {status.pending.map((userId) => (
                  <ReceiptRow key={`pending-${userId}`} userId={userId} at="" memberDirectory={memberDirectory} />
                ))}
              </Section>
            ) : null}
          </ScrollArea>
          <p className="border-t border-border/60 px-3 py-2 text-[11px] leading-snug text-muted-foreground">
            {T.deliveredMeaning}
          </p>
        </>
      ) : null}
    </>
  );
}
